/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



/**
 * useNavigationHistory
 *
 * A high-level navigation helper built on top of react-router and Redux:
 * - Tracks route transitions in Redux via `pushToHistory()`
 * - Provides `navigateBack()` consistent with browser back when possible
 * - Supports `navigateWithHistory()` to carry over state and a savedEntityId across screens
 * - Exposes helpers to read/clear the savedEntityId and fetch an entity lazily after navigation
 */
import { RoutesConstants } from '@malmur-technologies/medicalsystem-shared';
import { useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import useHttp from './http.hook';
import { HeaderEnum } from '@malmur-technologies/medicalsystem-shared';
import {
  pushToHistory,
  clearHistory,
  setSavedEntityId,
  clearSavedEntityId,
  handleBackNavigation,
  RouteHistoryEntry,
  selectNavigationHistory,
  selectSavedEntityId,
  selectCanGoBack,
  selectPreviousRoute,
} from '../store/slices/navigation.slice';
import { RootState } from 'store/store';

/** Configuration for on-demand entity fetch using a saved id */
interface EntityFetchConfig {
  apiUrl: string;
  onSuccess: (data: any) => void;
  onError?: (error: any) => void;
}

/** Public API of the navigation hook */
interface NavigationHistoryHook {
  /** navigate back using browser history if possible, otherwise custom stack */
  navigateBack: () => void;
  /** read the full Redux-kept history */
  getHistory: () => RouteHistoryEntry[];
  /** navigate while optionally persisting state and savedEntityId */
  navigateWithHistory: (to: string, options?: { replace?: boolean; state?: Record<string, unknown>; savedEntityId?: string }) => void;
  /** can user go back from the current route? */
  canGoBack: () => boolean;
  /** read the current savedEntityId without consuming it */
  getSavedEntityIdFromHistory: () => string | null;
  /** clear the savedEntityId once consumed */
  clearSavedEntityFromHistory: () => void;
  /** use the savedEntityId to fetch an entity; clears it on success */
  fetchSavedEntity: (config: EntityFetchConfig) => void;
}

export const useNavigationHistory = (): NavigationHistoryHook => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  // Track initial browser history length to decide if window.history.back() is safe
  const initialHistoryLength = useRef<number>(window.history.length);
  const isNavigatingBack = useRef<boolean>(false);
  const isUsingNavigateWithHistory = useRef<boolean>(false);

  // Local HTTP helper (used for lazy entity fetching)
  const [isLoading, _error, sendRequest] = useHttp();

  // Redux derived state
  const history = useSelector(selectNavigationHistory);
  const savedEntityId = useSelector(selectSavedEntityId);
  const canGoBackFromHistory = useSelector((state: RootState) => 
    selectCanGoBack(state, location.pathname)
  );
  const previousRoute = useSelector((state: RootState) => 
    selectPreviousRoute(state, location.pathname)
  );

  /** Return current history from Redux */
  const getHistory = useCallback((): RouteHistoryEntry[] => {
    return history;
  }, [history]);

  /**
   * Determines if we can go back:
   * - Prefer native browser back if user navigated within this session
   * - Otherwise rely on our Redux stack rules
   */
  const canGoBack = useCallback((): boolean => {
    const currentHistoryLength = window.history.length;
    const canUseHistoryBack = currentHistoryLength > initialHistoryLength.current;
    
    if (canUseHistoryBack) {
      return true;
    }
    
    return canGoBackFromHistory;
  }, [canGoBackFromHistory]);

  /**
   * On route changes, record an entry unless the change was initiated by our own back/navigateWithHistory flow.
   */
  useEffect(() => {
    if (isNavigatingBack.current || isUsingNavigateWithHistory.current) {
      isNavigatingBack.current = false;
      isUsingNavigateWithHistory.current = false;
      return;
    }

    const currentRoute = location.pathname;
    const locationKey = location.key;
    const state = location.state;
    
    dispatch(pushToHistory({
      route: currentRoute,
      key: locationKey,
      state,
    }));
  }, [location.pathname, location.key, location.state, dispatch]);

  /**
   * Navigate back either via browser history or via our Redux stack fallback.
   */
  const navigateBack = useCallback((): void => {
    const currentRoute = location.pathname;
    const currentHistoryLength = window.history.length;
    const canUseHistoryBack = currentHistoryLength > initialHistoryLength.current;
    
    if (canUseHistoryBack) {
      isNavigatingBack.current = true;
      window.history.back();
      return;
    }
    
    if (history.length === 0) {
      isNavigatingBack.current = true;
      navigate(RoutesConstants.DASHBOARD, { replace: true });
      dispatch(clearHistory());
      return;
    }
    
    if (previousRoute) {
      // Remove current route and jump to the previous one from Redux stack
      dispatch(handleBackNavigation(currentRoute));
      
      isNavigatingBack.current = true;
      navigate(previousRoute.route, { 
        state: previousRoute.state,
        replace: true 
      });
    } else {
      isNavigatingBack.current = true;
      navigate(RoutesConstants.DASHBOARD, { replace: true });
      dispatch(clearHistory());
    }
  }, [navigate, location.pathname, history.length, previousRoute, dispatch]);

  /**
   * Navigate and optionally persist a savedEntityId for the destination screen to consume.
   */
  const navigateWithHistory = useCallback((
    to: string, 
    options?: { replace?: boolean; state?: Record<string, unknown>; savedEntityId?: string }
  ): void => {
    if (options?.replace) {
      isNavigatingBack.current = true;
    }  
    isUsingNavigateWithHistory.current = true;
    
    if (options?.savedEntityId) {
      dispatch(setSavedEntityId(options.savedEntityId));
    }
    
    navigate(to, { 
      replace: options?.replace || false, 
      state: options?.state 
    });
  }, [navigate, dispatch]);

  /** Read the savedEntityId without consuming it */
  const getSavedEntityIdFromHistory = useCallback((): string | null => {
    return savedEntityId;
  }, [savedEntityId]);

  /** Clear the savedEntityId */
  const clearSavedEntityFromHistory = useCallback((): void => {
    dispatch(clearSavedEntityId());
  }, [dispatch]);

  /**
   * Fetch an entity using the saved id; on success invoke onSuccess and clear the saved id.
   */
  const fetchSavedEntity = useCallback((config: EntityFetchConfig): void => {
    const currentSavedEntityId = getSavedEntityIdFromHistory();
    if (!currentSavedEntityId) {
      return;
    }
    
    sendRequest(
      config.apiUrl,
      (data: any) => {
        config.onSuccess(data);
        clearSavedEntityFromHistory();
      },
      "GET",
      undefined,
      [{ header: HeaderEnum.RESOURCE_ID, value: currentSavedEntityId }]
    );
  }, [getSavedEntityIdFromHistory, clearSavedEntityFromHistory, sendRequest]);

  return {
    navigateBack,
    getHistory,
    navigateWithHistory,
    canGoBack,
    getSavedEntityIdFromHistory,
    clearSavedEntityFromHistory,
    fetchSavedEntity,
  };
};
